import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQueryParam } from "use-query-params";

import { EventForm } from "Bands/components";
import { useBandContext, usePermissionsContext } from "Bands/contexts";
import { BAND_PATHS } from "Bands/router";
import { clearActionMeta, createEvent, dataMetaSelector } from "Bands/state";
import { BAND_ACTIONS, IEvent, IIconBandSetting } from "Bands/types";
import { useBandRoute } from "Bands/utils";
import { addDays, format } from "date-fns";
import { copy } from "shared/assets";
import { addTemporaryToast } from "shared/components/Toast";
import { LocalStorageService } from "shared/services";
import { IAPIState, IFormButtons, ILocalStorageKey, IStatus } from "shared/types";
import { createNewDate, isValidDate } from "shared/utils";

const createCopy = copy.event.create;

interface CreateEventFormProps {
  eventTypes: IIconBandSetting[];
  placeholder: Partial<IEvent>;
  updatePlaceholder: (event: Partial<IEvent>) => void;
}

export const CreateEventForm: FC<CreateEventFormProps> = ({
  placeholder,
  updatePlaceholder,
  ...props
}) => {
  const { userCanManageBand } = usePermissionsContext();
  const [addAnother, setAddAnother] = useState(false);
  const [key, setKey] = useState(0);
  const dispatch = useDispatch();
  const { api: apiMeta, id: eventId } = useSelector(dataMetaSelector(BAND_ACTIONS.createEvent));
  const { pushRoute } = useBandRoute();

  const [date, setDate] = useQueryParam<string>("date");
  const validDateQuery = useMemo(() => isValidDate(date), [date]);
  const { band } = useBandContext();

  const lastUsedTimeZone =
    LocalStorageService.getItem(ILocalStorageKey.eventTimezone) || band?.timeZone;

  const [defaultValues, setDefaultValues] = useState(
    validDateQuery
      ? { date, timeZone: lastUsedTimeZone ?? undefined }
      : { timeZone: lastUsedTimeZone ?? undefined }
  );

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded && eventId) {
      dispatch(clearActionMeta(BAND_ACTIONS.createEvent));

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: createCopy.success,
        })
      );

      if (!addAnother) {
        eventId &&
          pushRoute(BAND_PATHS.BAND_EVENT, {
            params: { eventId },
          });
      } else {
        // clear the form and placeholder
        setKey(key + 1);

        setDate(format(addDays(createNewDate(date), 1), "yyyy-MM-dd"));

        updatePlaceholder({
          date: format(addDays(createNewDate(date), 1), "yyyy-MM-dd"),
        });

        setDefaultValues({
          date: format(addDays(createNewDate(date), 1), "yyyy-MM-dd"),
          timeZone: lastUsedTimeZone ?? undefined,
        });

        window.scrollTo({ top: 0, behavior: "smooth" });
      }

      // reset for the next submit
      setAddAnother(false);
    }
  }, [
    eventId,
    apiMeta,
    pushRoute,
    dispatch,
    addAnother,
    key,
    date,
    setDate,
    lastUsedTimeZone,
    updatePlaceholder,
  ]);

  const handleSubmit = (event: Omit<IEvent, "id">) => {
    // save last used timezone in local storage
    if (event.timeZone) {
      LocalStorageService.setItem(ILocalStorageKey.eventTimezone, event.timeZone);
    }

    dispatch(createEvent(event));
  };

  const createAndAddAnother = () => {
    setAddAnother(true);
  };

  const buttons: IFormButtons = useMemo(
    () =>
      ({
        primary: {
          children: createCopy.cta,
        },
        secondary: {
          children: createCopy.addAnotherCta,
          onClick: createAndAddAnother,
          isSubmitButton: true,
        },
      } as IFormButtons),
    []
  );

  return (
    <EventForm
      key={key}
      hasError={!!apiMeta?.error}
      apiStatus={apiMeta?.status}
      handleSubmit={handleSubmit}
      buttons={buttons}
      updatePlaceholder={updatePlaceholder}
      defaultValues={defaultValues}
      apiError={apiMeta?.error}
      canManageTypes={userCanManageBand}
      placeholder={placeholder}
      setDate={setDate}
      {...props}
    />
  );
};
